// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Content from 'components/Content';
import Section from 'components/Section';
import {
    formatFeatureAnnouncementDate,
    loadFeatureAnnouncements,
} from './newFeatures/featureAnnouncements';

const PenlyAccessNewFeatures = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;

        loadFeatureAnnouncements()
            .then((nextAnnouncements) => {
                if (!active) {
                    return;
                }

                setAnnouncements(nextAnnouncements);
                setError(null);
            })
            .catch((nextError) => {
                if (!active) {
                    return;
                }

                setAnnouncements([]);
                setError(nextError);
            })
            .finally(() => {
                if (active) {
                    setLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, []);

    const hasAnnouncements = announcements.length > 0;
    const registryCountLabel = useMemo(() => {
        if (loading) {
            return 'Indlæser registret...';
        }

        const count = announcements.length;
        return `${count} ${count === 1 ? 'opdatering' : 'opdateringer'} i registret`;
    }, [announcements.length, loading]);

    return (
        <Content>
            <Box sx={{ width: '100%', maxWidth: 960 }}>
                <Section
                    title="Nye features"
                    description={ (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={ 1 } alignItems={{ xs: 'flex-start', sm: 'center' }}>
                            <Typography variant="body2">
                                Her samler vi nye funktioner og produktopdateringer i Teal Business Access.
                            </Typography>
                            <Chip size="small" label={ registryCountLabel } />
                        </Stack>
                    ) }
                >
                    { loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress color="primary" size={ 32 } />
                        </Box>
                    ) }

                    { !loading && error && (
                        <Alert severity="error">
                            Kunne ikke indlæse feature-opdateringer lige nu.
                        </Alert>
                    ) }

                    { !loading && !error && !hasAnnouncements && (
                        <Typography variant="body1">
                            Der er ikke nogen nye features at vise endnu.
                        </Typography>
                    ) }

                    { !loading && !error && hasAnnouncements && (
                        <Stack spacing={ 2 }>
                            { announcements.map((announcement) => (
                                <Card key={ announcement.id } variant="outlined">
                                    <CardContent>
                                        <Stack spacing={ 2 }>
                                            <Stack spacing={ 1 }>
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={ 1 } alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                                    <Typography variant="h6">
                                                        { announcement.title }
                                                    </Typography>
                                                    <Chip size="small" label={ formatFeatureAnnouncementDate(announcement.publishedAt) } />
                                                </Stack>
                                                { announcement.summary && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        { announcement.summary }
                                                    </Typography>
                                                ) }
                                            </Stack>

                                            <Box
                                                sx={{
                                                    '& h1, & h2, & h3': { mt: 0 },
                                                    '& p:last-child': { mb: 0 },
                                                    '& ul': { pl: 3 },
                                                }}
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(announcement.content || '')) }}
                                            />

                                            { announcement.ctaLabel && announcement.ctaHref && (
                                                <Box>
                                                    <Button component="a" href={ announcement.ctaHref } target="_blank" rel="noreferrer" variant="outlined">
                                                        { announcement.ctaLabel }
                                                    </Button>
                                                </Box>
                                            ) }
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )) }
                        </Stack>
                    ) }
                </Section>
            </Box>
        </Content>
    );
};

export default PenlyAccessNewFeatures;
